import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Statistic,
  Select,
  Tag,
  DatePicker,
  Modal,
  Input,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SyncOutlined,
  DownloadOutlined,
  UnorderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useReconciliation } from "../../../hooks/finance/useReconciliation";
import ReconciliationTable from "../../../components/finance/reconciliation/ReconciliationTable";
import ReconciliationDetailDrawer from "../../../components/finance/reconciliation/ReconciliationDetailDrawer";
import CreateReconciliationModal from "../../../components/finance/reconciliation/CreateReconciliationModal";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ReconciliationManagement = () => {
  const {
    reconciliations,
    loading,
    fetchReconciliations,
    createReconciliation,
    completeReconciliation,
    markForReview,
    deleteReconciliation,
    getStatistics,
  } = useReconciliation();

  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [creating, setCreating] = useState(false);

  const [filters, setFilters] = useState({
    searchText: "",
    status: null,
    dateRange: null,
  });

  useEffect(() => {
    fetchReconciliations();
  }, []);

  useEffect(() => {
    const serverFilters = {};
    if (filters.status) serverFilters.status = filters.status;
    if (filters.dateRange && filters.dateRange.length === 2) {
      serverFilters.date_from = filters.dateRange[0].format("YYYY-MM-DD");
      serverFilters.date_to = filters.dateRange[1].format("YYYY-MM-DD");
    }

    fetchReconciliations({ filters: serverFilters });
  }, [filters.status, filters.dateRange]);

  const handleViewDetails = (reconciliation) => {
    setSelectedReconciliation(reconciliation);
    setDetailsDrawerVisible(true);
  };

  const handleComplete = async (reconciliation) => {
    Modal.confirm({
      title: "Complete Reconciliation",
      content: `Are you sure you want to mark the reconciliation for ${dayjs(reconciliation.reconciliationDate).format("DD MMM YYYY")} as completed?`,
      okText: "Yes, Complete",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        await completeReconciliation(reconciliation.reconciliationId);
      },
    });
  };

  const handleMarkForReview = async (reconciliation) => {
    Modal.confirm({
      title: "Mark for Review",
      content: `Are you sure you want to flag the reconciliation for ${dayjs(reconciliation.reconciliationDate).format("DD MMM YYYY")} for review?`,
      okText: "Yes, Flag for Review",
      okType: "default",
      cancelText: "Cancel",
      onOk: async () => {
        await markForReview(reconciliation.reconciliationId);
      },
    });
  };

  const handleDelete = async (reconciliation) => {
    Modal.confirm({
      title: "Delete Reconciliation",
      content: `Are you sure you want to delete the reconciliation for ${dayjs(reconciliation.reconciliationDate).format("DD MMM YYYY")}? This action cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        await deleteReconciliation(reconciliation.reconciliationId);
      },
    });
  };

  const handleCreateReconciliation = async (data) => {
    setCreating(true);
    try {
      const result = await createReconciliation(data);
      if (result) {
        setCreateModalVisible(false);
        return true;
      }
      return false;
    } finally {
      setCreating(false);
    }
  };

  // Get statistics
  const stats = getStatistics();

  // Filter reconciliations by search text (client-side)
  const filteredReconciliations = reconciliations.filter((rec) => {
    if (!filters.searchText) return true;
    const search = filters.searchText.toLowerCase();
    return (
      rec.reconciliationDate?.toLowerCase().includes(search) ||
      rec.status?.toLowerCase().includes(search) ||
      rec.notes?.toLowerCase().includes(search)
    );
  });

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredReconciliations.map((rec) => ({
      Date: rec.reconciliationDate
        ? dayjs(rec.reconciliationDate).format("DD MMM YYYY")
        : "-",
      "Total Payments": parseFloat(rec.totalPayments || 0),
      "Valid Payments": parseFloat(rec.totalValidPayments || 0),
      "Suspense Payments": parseFloat(rec.totalSuspensePayments || 0),
      "Payment Count": rec.paymentCount || 0,
      "Suspense Count": rec.suspenseCount || 0,
      "Reconciliation Rate (%)":
        rec.totalPayments > 0
          ? Math.floor(
              (parseFloat(rec.totalValidPayments) /
                parseFloat(rec.totalPayments)) *
                100,
            )
          : 0,
      Status: rec.status,
      Notes: rec.notes || "-",
      "Created At": rec.createdAt
        ? dayjs(rec.createdAt).format("DD MMM YYYY HH:mm")
        : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reconciliations");

    const fileName = `reconciliations_${dayjs().format("YYYY-MM-DD_HHmmss")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    message.success("Export completed successfully");
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Reconciliations"
                value={stats.total}
                valueStyle={{ color: "#1890ff" }}
                prefix={<UnorderedListOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Completed"
                value={stats.completed}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="In Progress"
                value={stats.inProgress}
                valueStyle={{ color: "#1890ff" }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Requires Review"
                value={stats.requiresReview}
                valueStyle={{ color: "#faad14" }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Aggregate Financial Summary */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Valid Payments"
                value={formatCurrency(stats.totalValidPayments)}
                prefix="UGX"
                valueStyle={{ color: "#52c41a", fontSize: "20px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Payments Pending Validation"
                value={formatCurrency(stats.totalPendingPayments)}
                prefix="UGX"
                valueStyle={{ color: "#faad14", fontSize: "20px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Suspense"
                value={formatCurrency(stats.totalSuspensePayments)}
                prefix="UGX"
                valueStyle={{ color: "#ff4d4f", fontSize: "20px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Payments Processed"
                value={stats.totalPaymentCount}
                valueStyle={{ fontSize: "20px" }}
                suffix={
                  <span style={{ fontSize: "14px", color: "#888" }}>
                    ({stats.totalSuspenseCount} suspense)
                  </span>
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content Card */}
        <Card
          title={
            <Space>
              <UnorderedListOutlined />
              <span>Daily Reconciliations</span>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Action Buttons */}
            <Row gutter={[8, 8]}>
              <Col xs={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                  >
                    New Reconciliation
                  </Button>
                  <Button
                    icon={<SyncOutlined />}
                    onClick={() => fetchReconciliations()}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={exportToExcel}
                    disabled={filteredReconciliations.length === 0}
                  >
                    Export to Excel
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Filters */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Search by date, status, or notes..."
                  prefix={<SearchOutlined />}
                  value={filters.searchText}
                  onChange={(e) =>
                    setFilters({ ...filters, searchText: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filter by Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                >
                  <Option value="Completed">Completed</Option>
                  <Option value="In Progress">In Progress</Option>
                  <Option value="Requires Review">Requires Review</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={10}>
                <RangePicker
                  style={{ width: "100%" }}
                  value={filters.dateRange}
                  onChange={(dates) =>
                    setFilters({ ...filters, dateRange: dates })
                  }
                  format="YYYY-MM-DD"
                  placeholder={["From Date", "To Date"]}
                />
              </Col>
            </Row>

            {/* Active Filters Indicator */}
            {(filters.searchText || filters.status || filters.dateRange) && (
              <div>
                <Tag color="blue">
                  Showing {filteredReconciliations.length} of{" "}
                  {reconciliations.length} reconciliations
                </Tag>
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    setFilters({
                      searchText: "",
                      status: null,
                      dateRange: null,
                    })
                  }
                >
                  Clear Filters
                </Button>
              </div>
            )}

            {/* Reconciliation Table */}
            <ReconciliationTable
              reconciliations={filteredReconciliations}
              loading={loading}
              onViewDetails={handleViewDetails}
              onComplete={handleComplete}
              onMarkForReview={handleMarkForReview}
              onDelete={handleDelete}
            />
          </Space>
        </Card>
      </Space>

      {/* Detail Drawer */}
      <ReconciliationDetailDrawer
        visible={detailsDrawerVisible}
        onClose={() => {
          setDetailsDrawerVisible(false);
          setSelectedReconciliation(null);
        }}
        reconciliationId={selectedReconciliation?.reconciliationId}
        onComplete={handleComplete}
        onMarkForReview={handleMarkForReview}
      />

      {/* Create Modal */}
      <CreateReconciliationModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSubmit={handleCreateReconciliation}
        loading={creating}
      />
    </>
  );
};

export default ReconciliationManagement;
